import { useEffect, useState } from 'react'
import { api } from './api'

/** Register the service worker (production builds only). */
export function registerServiceWorker() {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) return
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => undefined) })
}

/** Send uncaught browser errors to /api/v2/client-errors (max 5 per page load). */
export function installErrorReporter() {
  let sent = 0
  const send = (message: string, stack?: string, kind: 'error' | 'unhandledrejection' = 'error') => {
    if (sent >= 5 || !message) return
    sent++
    const body = JSON.stringify({ message: message.slice(0, 500), stack: stack?.slice(0, 4000), url: location.pathname.slice(0, 500), kind,
      release: document.querySelector('meta[name="build-id"]')?.getAttribute('content') ?? undefined })
    fetch('/api/v2/client-errors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => undefined)
  }
  window.addEventListener('error', e => send(e.message, e.error instanceof Error ? e.error.stack : undefined))
  window.addEventListener('unhandledrejection', e => {
    const r: unknown = e.reason
    send(r instanceof Error ? r.message : String(r), r instanceof Error ? r.stack : undefined, 'unhandledrejection')
  })
}

interface BeforeInstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); deferred = e as BeforeInstallPromptEvent; listeners.forEach(l => l()) })
  window.addEventListener('appinstalled', () => { deferred = null; listeners.forEach(l => l()) })
}
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent)

/** Install prompt state: `native` (Chrome/Edge/Android), `ios` (Share → Add to Home Screen), or none. */
export function useInstallPrompt() {
  const [, force] = useState(0)
  useEffect(() => { const l = () => force(n => n + 1); listeners.add(l); return () => { listeners.delete(l) } }, [])
  if (standalone()) return { mode: 'installed' as const, install: async () => false }
  if (deferred) return { mode: 'native' as const, install: async () => { const d = deferred!; await d.prompt(); const c = await d.userChoice; deferred = null; force(n => n + 1); return c.outcome === 'accepted' } }
  if (isIos()) return { mode: 'ios' as const, install: async () => false }
  return { mode: 'none' as const, install: async () => false }
}

// ── Web Push
const b64ToUint8 = (b64: string) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4)
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, c => c.charCodeAt(0))
}
export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

export async function pushStatus(): Promise<'unsupported' | 'denied' | 'on' | 'off'> {
  if (!pushSupported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  return sub ? 'on' : 'off'
}

export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false
  if ((await Notification.requestPermission()) !== 'granted') return false
  const reg = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready
  const { key } = await api<{ key: string }>('/push/key')
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(key) })
  const j = sub.toJSON()
  await api('/push/subscriptions', { method: 'POST', body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys }) })
  return true
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (!sub) return
  await api('/push/subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => undefined)
  await sub.unsubscribe()
}
